/**
 * Dialog Auth
 * Encapsulates Office Dialog API for auth flow.
 */

/* global Office */

import type { AuthMessage, AuthSession } from "./types";

export class AuthDialogError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "AuthDialogError";
    this.code = code;
  }
}

function isValidAuthSession(obj: unknown): obj is AuthSession {
  if (!obj || typeof obj !== "object") return false;
  const o = obj as Record<string, unknown>;
  const user = o.user;
  if (!user || typeof user !== "object") return false;
  const u = user as Record<string, unknown>;
  if (typeof u.id !== "string" || typeof u.email !== "string") return false;
  if (typeof o.access_token !== "string" || typeof o.refresh_token !== "string") return false;
  if (typeof o.expires_at !== "number") return false;
  return true;
}

export function parseDialogMessage(raw: string): AuthMessage | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const p = parsed as Record<string, unknown>;
    const type = p.type;
    if (type === "AUTH_COMPLETE") {
      const session = p.session;
      if (!isValidAuthSession(session)) return null;
      return { type: "AUTH_COMPLETE", session };
    }
    if (type === "AUTH_ERROR") {
      const message = typeof p.message === "string" ? p.message : "Unknown error";
      const code = typeof p.code === "string" ? p.code : undefined;
      return { type: "AUTH_ERROR", code, message };
    }
    if (type === "AUTH_CANCELLED") {
      return { type: "AUTH_CANCELLED" };
    }
    return null;
  } catch {
    return null;
  }
}

export function openAuthDialog(dialogUrl: string): Promise<AuthSession> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 50, width: 30, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new AuthDialogError("DIALOG_OPEN_FAILED", "Failed to open sign-in dialog"));
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          const msg = parseDialogMessage((arg as { message: string }).message);
          if (!msg) return;
          dialog.close();
          if (msg.type === "AUTH_COMPLETE") {
            resolve(msg.session);
          } else if (msg.type === "AUTH_ERROR") {
            reject(new AuthDialogError(msg.code || "AUTH_ERROR", msg.message));
          } else {
            reject(new AuthDialogError("AUTH_CANCELLED", "Sign-in was cancelled"));
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          dialog.close();
          reject(new AuthDialogError("AUTH_CANCELLED", "Sign-in was cancelled"));
        });
      }
    );
  });
}
